// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, Chip, Drawer, Grid, Tooltip, Typography, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import LogoSection from '../LogoSection';
import ProfileSection from './ProfileSection';
import BorderRadius from '../../Customization/BorderRadius';
import Layout from '../../Customization/Layout';
import PresetColor from '../../Customization/PresetColor';
import InputFilled from '../../Customization/InputFilled';
import BoxContainer from '../../Customization/BoxContainer';
import { gridSpacing } from 'store/constant';

import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';

// assets
import { IconMenu2, IconSettings } from '@tabler/icons';

import { useState } from 'react';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = () => {
    const theme = useTheme();

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const { layout } = useConfig();

    const [settingsOpen, setSettingsOpen] = useState(false);
    const handleSettingsToggle = () => setSettingsOpen((prev) => !prev);

    return (
        <>
            {/* logo & toggler button */}
            <Box
                sx={{
                    width: 228,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    [theme.breakpoints.down('md')]: { width: 'auto' }
                }}
            >
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <LogoSection />
                </Box>

                {layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            overflow: 'hidden',
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                            color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
                            '&:hover': {
                                background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
                                color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.light
                            }
                        }}
                        onClick={() => dispatch(openDrawer(!drawerOpen))}
                        color="inherit"
                    >
                        <IconMenu2 stroke={1.5} size="20px" />
                    </Avatar>
                ) : null}
            </Box>

            {/* center brand name */}
            <Box
                sx={{
                    flexGrow: 1,
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography
                    sx={{
                        fontFamily: '"Raleway", sans-serif',
                        fontWeight: 800,
                        fontSize: '2rem',
                        letterSpacing: '0.5px',
                        lineHeight: 1.2,
                        color: '#5e35b1'
                    }}
                >
                    Nexus CX
                </Typography>
            </Box>

            {/* mobile spacer — pushes right icons to edge when center title is hidden */}
            <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />

            {/* Settings chip — same style as profile */}
            <Tooltip title="Layout">
                <Chip
                    sx={{
                        height: '48px',
                        alignItems: 'center',
                        borderRadius: '27px',
                        mr: 1,
                        transition: 'all .2s ease-in-out',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        '&:hover': {
                            borderColor: theme.palette.primary.main,
                            background: `${theme.palette.primary.main}!important`,
                            color: theme.palette.primary.light,
                            '& svg': { stroke: theme.palette.primary.light }
                        },
                        '& .MuiChip-label': { lineHeight: 0 }
                    }}
                    label={<IconSettings stroke={1.5} size="28px" color={theme.palette.primary.main} />}
                    variant="outlined"
                    onClick={handleSettingsToggle}
                    color="primary"
                />
            </Tooltip>

            {/* Settings Drawer */}
            <Drawer anchor="right" open={settingsOpen} onClose={handleSettingsToggle} PaperProps={{ sx: { width: 280 } }}>
                {settingsOpen && (
                    <PerfectScrollbar component="div">
                        <Grid container spacing={gridSpacing} sx={{ p: 3 }}>
                            <Grid item xs={12}>
                                <Layout />
                            </Grid>
                            <Grid item xs={12}>
                                <PresetColor />
                            </Grid>
                            <Grid item xs={12}>
                                <BorderRadius />
                            </Grid>
                            <Grid item xs={12}>
                                <InputFilled />
                            </Grid>
                            <Grid item xs={12}>
                                <BoxContainer />
                            </Grid>
                        </Grid>
                    </PerfectScrollbar>
                )}
            </Drawer>

            <ProfileSection />
        </>
    );
};

export default Header;
